import debounce from 'lodash/debounce';
// import { nodesBBox } from './NodesBBoxPlugin';

/**
 * Level of Detail (LOD) Plugin for Rete.js
 * Improves performance by:
 * 1. Hiding nodes outside the viewport
 * 2. Simplifying nodes at lower zoom levels
 * 3. Reducing DOM updates during pan/zoom operations
 */

// Structural node types that should never be simplified or hidden
const STRUCTURAL_NODE_TYPES = ['Group', 'Frame', 'Comment', 'Note', 'Section'];

class LodPlugin {
  constructor(editor, options = {}) {
    this.editor = editor;
    this.options = {
      // Zoom threshold below which nodes are simplified
      simplifyThreshold: 0.2,
      // Zoom threshold below which nodes are hidden
      hideThreshold: 0.15,
      // Viewport padding (nodes this far outside viewport are still rendered)
      viewportPadding: 200,
      // Update frequency in milliseconds
      updateFrequency: 500, // ~60fps
      // Whether to enable viewport culling
      enableViewportCulling: true,
      // Whether to enable zoom-based simplification
      enableZoomSimplification: true,
      // Node types that should never be simplified (e.g., currently selected)
      excludeFromSimplification: ['Group', 'Frame', 'Comment'],
      // Adaptive case size logic
      enableAdaptiveCaseSize: true,
      // Case size threshold below which simplification is disabled
      smallCaseThreshold: 100,
      // Interleaved rendering options
      enableInterleavedRendering: true,
      // Time budget per frame in milliseconds
      renderBudgetMs: 32,
      // Maximum nodes to process per batch
      maxNodesPerBatch: 10,
      // Delay between batches in milliseconds
      batchDelayMs: 1,
      // Prioritize selected nodes for rendering
      prioritizeSelectedNodes: true,
      // Dynamic viewport padding options
      enableDynamicPadding: true,
      maxPaddingMultiplier: 4.0, // Maximum padding multiplier at closest zoom
      minPaddingMultiplier: 0.3, // Minimum padding multiplier at farthest zoom
      // Adaptive padding based on user behavior
      enableAdaptivePadding: true,
      panVelocityThreshold: 50, // Pixels per frame to consider "fast panning"
      adaptivePaddingBoost: 1.5, // Additional multiplier during fast panning
      panHistorySize: 10, // Number of recent pan events to track for velocity calculation
      ...options
    };

    this.isEnabled = true;
    this.currentZoom = 1;
    this.viewport = { x: 0, y: 0, width: 0, height: 0 };
    this.nodeStates = new Map(); // Track node visibility and detail states
    
    // Interleaved rendering state
    this.renderQueue = new Set(); // Nodes waiting to be rendered complex
    this.isProcessingQueue = false;
    this.userInteractionDetected = false;
    this.lastInteractionTime = 0;
    
    // Pan velocity tracking for adaptive padding
    this.panHistory = []; // Array of { timestamp, x, y } entries
    this.currentPanVelocity = 0;
    this.lastViewportPosition = { x: 0, y: 0 };
    
    // Performance tracking
    this.performanceMetrics = {
      directCssManipulations: 0,
      vueReactivityTriggers: 0,
      cacheHits: 0,
      cacheMisses: 0,
      lastUpdateTime: 0,
      interleavedBatches: 0,
      nodesRenderedProgressively: 0
    };
    
    this.init();
  }

  init() {
    // Debounced update function to prevent excessive calculations
    this.debouncedUpdate = debounce(() => {
      if (this.isEnabled) {
        this.updateLod();
      }
    }, this.options.updateFrequency);

    this.setupEventListeners();
    this.injectStyles();
    
    // Initialize interleaved rendering if enabled
    if (this.options.enableInterleavedRendering) {
      this.initializeInterleavedRendering();
    }
  }

  setupEventListeners() {
    // Listen to zoom and pan events
    this.editor.on('zoom', ({ zoom, area }) => {
      this.currentZoom = zoom;
      this.updateViewport(area);
      this.debouncedUpdate();
    });

    this.editor.on('translate', ({ area }) => {
      this.updateViewport(area);
      this.trackPanVelocity();
      this.debouncedUpdate();
    });

    // Initialize LOD state immediately when node is created (before rendering)
    this.editor.on('nodecreate', (node) => {
      this.initializeNodeLodState(node);
    });

    // Clean up state when node is removed
    this.editor.on('noderemoved', (node) => {
      this.nodeStates.delete(node.id);
      // Remove from render queue if present
      this.renderQueue.delete(node.id);
    });

    // User interaction detection for interleaved rendering
    if (this.options.enableInterleavedRendering) {
      this.setupInteractionDetection();
    }

    // Update element reference when node is rendered
    this.editor.on('rendernode', ({ el, node }) => {
      this.updateNodeElement(node, el);
    });

    // Handle window resize
    window.addEventListener('resize', this.debouncedUpdate);
  }

  updateViewport(area) {
    if (!area || !area.container) return;
    
    const container = area.container;
    const transform = area.transform;
    
    // Store previous viewport position for velocity calculation
    this.lastViewportPosition = { 
      x: this.viewport.x, 
      y: this.viewport.y 
    };
    
    this.viewport = {
      x: -transform.x / transform.k,
      y: -transform.y / transform.k,
      width: container.clientWidth / transform.k,
      height: container.clientHeight / transform.k
    };
  }

  trackPanVelocity() {
    if (!this.options.enableAdaptivePadding) return;
    
    const now = performance.now();
    const deltaX = this.viewport.x - this.lastViewportPosition.x;
    const deltaY = this.viewport.y - this.lastViewportPosition.y;
    
    // Add current pan data to history
    this.panHistory.push({
      timestamp: now,
      deltaX,
      deltaY,
      distance: Math.sqrt(deltaX * deltaX + deltaY * deltaY)
    });
    
    // Keep only recent history
    const cutoffTime = now - 500; // Keep last 500ms of data
    this.panHistory = this.panHistory.filter(entry => entry.timestamp > cutoffTime);
    
    // Calculate current velocity (distance per millisecond)
    if (this.panHistory.length >= 2) {
      const recent = this.panHistory.slice(-Math.min(this.options.panHistorySize, this.panHistory.length));
      const timeSpan = recent[recent.length - 1].timestamp - recent[0].timestamp;
      const totalDistance = recent.reduce((sum, entry) => sum + entry.distance, 0);
      
      this.currentPanVelocity = timeSpan > 0 ? (totalDistance / timeSpan) : 0; // pixels per ms
    }
  }

  initializeNodeLodState(node) {
    // Initialize LOD state immediately when node is created (before rendering)
    const shouldBeSimplified = true;
    
    this.nodeStates.set(node.id, {
      element: null, // Will be set when node is rendered
      isVisible: true, // Assume visible initially
      isSimplified: shouldBeSimplified,
      originalDisplay: '', // Will be set when element is available
      bbox: null,
      hasBeenRenderedComplex: false // Track if node has ever been rendered in complex mode
    });

    // Set the LOD state on the node for Vue components to access
    node._lodState = {
      isSimplified: shouldBeSimplified,
      currentZoom: this.currentZoom,
      simplifyThreshold: this.options.simplifyThreshold,
      hasBeenRenderedComplex: false,
      useHybridRendering: true // Flag to enable hybrid v-if/v-show behavior
    };
  }

  updateNodeElement(node, el) {
    // Update the element reference and originalDisplay when node is rendered
    const state = this.nodeStates.get(node.id);
    if (!state) {
      // Fallback: if state doesn't exist, initialize it
      this.initializeNodeLodState(node);
      const newState = this.nodeStates.get(node.id);
      newState.element = el;
      newState.originalDisplay = el.style.display || '';
    } else {
      state.element = el;
      state.originalDisplay = el.style.display || '';
    }

    // Apply initial LOD state to the rendered element
    this.applyLodStateToElement(node, el);
  }

  applyLodStateToElement(node, el) {
    const state = this.nodeStates.get(node.id);
    if (!state) return;

    // Apply simplification state
    if (this.options.enableZoomSimplification) {
      this.setNodeSimplification(el, node, state.isSimplified);
    }

    // Apply visibility state
    if (this.options.enableViewportCulling) {
      this.setNodeVisibility(el, node, state.isVisible);
    }
  }

  updateLod() {
    const startTime = performance.now();
    const area = this.editor.view.area;
    if (!area) return;

    this.updateViewport(area);

    this.editor.nodes.forEach(node => {
      const nodeView = this.editor.view.nodes.get(node);
      if (!nodeView || !nodeView.el) return;

      const state = this.nodeStates.get(node.id);
      if (!state) {
        // Fallback: if state doesn't exist, initialize it
        this.initializeNodeLodState(node);
        this.updateNodeElement(node, nodeView.el);
        return;
      }

      // Ensure element reference is set
      if (!state.element) {
        this.updateNodeElement(node, nodeView.el);
      }

      this.updateNodeLod(node, nodeView, state);
    });

    this.performanceMetrics.lastUpdateTime = performance.now() - startTime;
  }

  updateNodeLod(node, nodeView, state) {
    // Skip LOD processing for structural nodes - they should always be visible and never simplified
    if (STRUCTURAL_NODE_TYPES.includes(node.name)) {
      // Ensure structural nodes are always visible and not simplified
      this.setNodeVisibility(nodeView.el, node, true);
      this.setNodeSimplification(nodeView.el, node, false);
      state.isVisible = true;
      state.isSimplified = false;
      return;
    }

    const shouldBeVisible = this.shouldNodeBeVisible(node);
    const shouldBeSimplified = this.shouldNodeBeSimplified(node);
    const isSmallCase = this.options.enableAdaptiveCaseSize && this.isSmallCase();

    // Update visibility
    if (this.options.enableViewportCulling) {
      if (shouldBeVisible !== state.isVisible) {
        this.setNodeVisibility(nodeView.el, node, shouldBeVisible);
        state.isVisible = shouldBeVisible;
      }
    }

    // Update simplification only for visible nodes
    // For small cases, skip simplification entirely but continue with visibility checks
    if (shouldBeVisible && this.options.enableZoomSimplification && !isSmallCase) {
      if (shouldBeSimplified !== state.isSimplified) {
        // Track if node is becoming complex (not simplified) for the first time
        if (!shouldBeSimplified && !state.hasBeenRenderedComplex) {
          if (this.options.enableInterleavedRendering) {
            // Add to render queue for progressive rendering
            this.addToRenderQueue(node);
          } else {
            // Immediate rendering (original behavior)
            state.hasBeenRenderedComplex = true;
          }
        }

        // Use CSS class for simplification
        if (state.hasBeenRenderedComplex) {
          // Always skip Vue updates for nodes that have been rendered complex
            this.setNodeSimplification(nodeView.el, node, shouldBeSimplified);
            this.performanceMetrics.directCssManipulations++;
          // }
          // Always skip Vue updates for already-rendered nodes
          this.notifyVueComponent(node, shouldBeSimplified, state.hasBeenRenderedComplex, true);
        } else {
          // First-time render: use Vue reactivity
          this.setNodeSimplification(nodeView.el, node, shouldBeSimplified);
          // Notify the Vue component about LOD state change
          this.notifyVueComponent(node, shouldBeSimplified, state.hasBeenRenderedComplex);
          this.performanceMetrics.vueReactivityTriggers++;
          this.performanceMetrics.cacheMisses++;
        }

        state.isSimplified = shouldBeSimplified;
      }
    } else if (shouldBeVisible && isSmallCase) {
      // For small cases, ensure all visible nodes are rendered in full detail
      if (state.isSimplified) {
        // Force nodes to complex state in small cases
        if (!state.hasBeenRenderedComplex) {
          if (this.options.enableInterleavedRendering) {
            // Add to render queue for progressive rendering
            this.addToRenderQueue(node);
          } else {
            // Immediate rendering (original behavior)
            state.hasBeenRenderedComplex = true;
          }
        }
        
        // Ensure node is not simplified
        this.setNodeSimplification(nodeView.el, node, false);
        this.notifyVueComponent(node, false, state.hasBeenRenderedComplex, state.hasBeenRenderedComplex);
        state.isSimplified = false;
      }
    }
  }

  shouldNodeBeVisible(node) {
    // Always show selected nodes
    if (this.editor.selected.contains(node)) {
      return true;
    }

    // Always show structural nodes regardless of viewport position
    if (STRUCTURAL_NODE_TYPES.includes(node.name)) {
      return true;
    }

    // Check if zoom is too low
    // if (this.currentZoom < this.options.hideThreshold) {
    //   return false;
    // }

    // Check viewport intersection
    return this.isNodeInViewport(node);
  }

  shouldNodeBeSimplified(node) {
    // Never simplify selected nodes
    if (this.editor.selected.contains(node)) {
      return false;
    }

    // Never simplify structural nodes
    if (STRUCTURAL_NODE_TYPES.includes(node.name)) {
      return false;
    }

    // Check excluded types from options (for backward compatibility)
    if (this.options.excludeFromSimplification.includes(node.name)) {
      return false;
    }

    // Adaptive case size: disable simplification for small cases
    if (this.options.enableAdaptiveCaseSize && this.isSmallCase()) {
      return false;
    }

    // Simplify based on zoom level
    return this.currentZoom < this.options.simplifyThreshold;
  }

  /**
   * Check if the current case is considered "small" (has fewer nodes than threshold)
   * Small cases disable simplification but keep viewport culling for performance
   */
  isSmallCase() {
    const totalNodes = this.editor.nodes ? this.editor.nodes.length : 0;
    return totalNodes < this.options.smallCaseThreshold;
  }

  isNodeInViewport(node) {
    // Dynamic buffering based on zoom level
    const basePadding = this.options.viewportPadding;
    const dynamicPadding = this.calculateDynamicViewportPadding(basePadding);
    
    const nodePos = node.position;
    
    // Rough node size estimation (you might want to cache actual sizes)
    const nodeWidth = 200; // Default node width
    const nodeHeight = 100; // Default node height

    const nodeLeft = nodePos[0];
    const nodeTop = nodePos[1];
    const nodeRight = nodeLeft + nodeWidth;
    const nodeBottom = nodeTop + nodeHeight;

    // Create buffered viewport bounds
    const viewLeft = this.viewport.x - dynamicPadding;
    const viewTop = this.viewport.y - dynamicPadding;
    const viewRight = this.viewport.x + this.viewport.width + dynamicPadding;
    const viewBottom = this.viewport.y + this.viewport.height + dynamicPadding;

    // Check intersection using standard AABB (Axis-Aligned Bounding Box) test
    const intersects = !(nodeRight < viewLeft || 
                        nodeLeft > viewRight || 
                        nodeBottom < viewTop || 
                        nodeTop > viewBottom);

    // Optional debug logging (can be enabled via options)
    if (this.options.debugViewportCulling) {
      console.log(`Node ${node.id} viewport check:`, {
        nodeBox: { left: nodeLeft, top: nodeTop, right: nodeRight, bottom: nodeBottom },
        viewBox: { left: viewLeft, top: viewTop, right: viewRight, bottom: viewBottom },
        dynamicPadding: Math.round(dynamicPadding),
        zoom: Math.round(this.currentZoom * 100) / 100,
        intersects
      });
    }

    return intersects;
  }

  calculateDynamicViewportPadding(basePadding) {
    // Skip dynamic padding if disabled
    if (!this.options.enableDynamicPadding) {
      return basePadding;
    }

    // Calculate dynamic padding based on zoom level
    // At high zoom (close in), users pan more aggressively and need more buffering
    // At low zoom (far out), users pan less aggressively and need less buffering
    
    const zoom = this.currentZoom;
    
    // Enhanced zoom thresholds with more granular control
    const zoomThresholds = [
      { zoom: 3.0, multiplier: 4.0 },   // Extreme close zoom: 4x padding
      { zoom: 2.5, multiplier: 3.5 },   // Very close zoom: 3.5x padding
      { zoom: 2.0, multiplier: 3.0 },   // Close zoom: 3x padding
      { zoom: 1.5, multiplier: 2.5 },   // Medium-close zoom: 2.5x padding
      { zoom: 1.0, multiplier: 2.0 },   // Normal zoom: 2x padding
      { zoom: 0.7, multiplier: 1.5 },   // Medium zoom: 1.5x padding
      { zoom: 0.5, multiplier: 1.0 },   // Far zoom: normal padding
      { zoom: 0.3, multiplier: 0.7 },   // Very far zoom: 0.7x padding
      { zoom: 0.15, multiplier: 0.5 },  // Extremely far zoom: 0.5x padding
      { zoom: 0.1, multiplier: 0.3 }    // Maximum far zoom: 0.3x padding
    ];
    
    // Find the appropriate multiplier based on current zoom
    let multiplier = 1.0; // Default multiplier
    
    for (let i = 0; i < zoomThresholds.length - 1; i++) {
      const current = zoomThresholds[i];
      const next = zoomThresholds[i + 1];
      
      if (zoom >= next.zoom) {
        if (zoom >= current.zoom) {
          // Use current threshold multiplier
          multiplier = current.multiplier;
        } else {
          // Smooth interpolation between current and next threshold
          const zoomRange = current.zoom - next.zoom;
          const zoomOffset = zoom - next.zoom;
          const interpolationFactor = zoomOffset / zoomRange;
          
          // Use smooth curve for more natural transitions
          const smoothFactor = 0.5 * (1 + Math.cos(Math.PI * (1 - interpolationFactor)));
          
          multiplier = next.multiplier + 
            (current.multiplier - next.multiplier) * smoothFactor;
        }
        break;
      }
    }
    
    // If zoom is below the lowest threshold, use the lowest multiplier
    if (zoom < zoomThresholds[zoomThresholds.length - 1].zoom) {
      multiplier = zoomThresholds[zoomThresholds.length - 1].multiplier;
    }
    
    // Apply adaptive padding boost based on pan velocity
    if (this.options.enableAdaptivePadding && this.currentPanVelocity > 0) {
      const velocityBoost = this.calculateVelocityBoost();
      multiplier *= velocityBoost;
    }
    
    // Calculate final padding with configurable minimum and maximum bounds
    const dynamicPadding = basePadding * multiplier;
    const minPadding = basePadding * this.options.minPaddingMultiplier;
    const maxPadding = basePadding * this.options.maxPaddingMultiplier;
    
    return Math.max(minPadding, Math.min(maxPadding, dynamicPadding));
  }

  calculateVelocityBoost() {
    // Convert velocity from pixels/ms to pixels/frame (assuming 60fps)
    const velocityPerFrame = this.currentPanVelocity * (1000 / 60);
    
    if (velocityPerFrame < this.options.panVelocityThreshold) {
      return 1.0; // No boost for slow panning
    }
    
    // Calculate boost factor based on velocity
    // Higher velocities get exponentially higher boost up to the maximum
    const velocityRatio = velocityPerFrame / this.options.panVelocityThreshold;
    const maxBoost = this.options.adaptivePaddingBoost;
    
    // Use logarithmic scaling to prevent excessive buffering
    const boost = 1.0 + (maxBoost - 1.0) * Math.min(1.0, Math.log(1 + velocityRatio) / Math.log(2));
    
    return boost;
  }

  setNodeVisibility(el, node, visible) {
    const nodeConnections = [...node.inputs.values(), ...node.outputs.values()]
    const nodeConnectionEls = nodeConnections.flatMap(io => io.connections.flatMap(connection => connection._el));
    if (visible) {
      el.style.display = '';
      el.style.visibility = 'visible';
      nodeConnectionEls.forEach(el => {
        el.style.display = '';
        el.style.visibility = 'visible';
      });
    } else {
      el.style.display = 'none';
      nodeConnectionEls.forEach(el => {
        el.style.display = 'none';
      });
    }
  }

  setNodeSimplification(el, node, simplified) {
    if (simplified) {
      el.classList.add('lod-simplified');
      // Update connections when switching to simplified state to prevent misalignment
      setTimeout(() => {
        console.debug('Updating connections for node', node.id)
        this.editor.view.updateConnections({ node });
      }, 0);
    } else {
      el.classList.remove('lod-simplified');
      // Update connections when switching back to complex state to prevent misalignment
      setTimeout(() => {
        this.editor.view.updateConnections({ node });
      }, 0);
    }
  }

  notifyVueComponent(node, isSimplified, hasBeenRenderedComplex, skipUpdate = false) {
    // Update the node's LOD state so Vue components can access it
    node._lodState = {
      isSimplified,
      currentZoom: this.currentZoom,
      simplifyThreshold: this.options.simplifyThreshold,
      hasBeenRenderedComplex,
      useHybridRendering: true
    };
    
    // Only trigger Vue update if we're not using direct CSS manipulation
    if (!skipUpdate && node.vueContext && node.vueContext.$forceUpdate) {
      node.vueContext.$forceUpdate();
      setTimeout(() => {
        this.editor.view.updateConnections({ node });
      })
    }
  }

  injectStyles() {
    if (document.getElementById('lod-plugin-styles')) return;

    const styles = `
      /* Hide complex elements within simplified nodes */
      .lod-simplified .node-content,
      .lod-simplified .form-group,
      .lod-simplified .input-group,
      .lod-simplified .button-group,
      .lod-simplified .status-indicator,
      .lod-simplified .metadata,
      .lod-simplified .description,
      .lod-simplified .controls,
      .lod-simplified .control,
      .lod-simplified .advanced-options,
      .lod-simplified .parameter-list,
      .lod-simplified .value-display,
      .lod-simplified .validation-message,
      .lod-simplified .check-controls-dropdown,
      .lod-simplified .new-check-button,
      .lod-simplified .remove-control-btn,
      .lod-simplified .segmented-select,
      .lod-simplified .md-menu,
      .lod-simplified .md-button,
      .lod-simplified .md-field,
      .lod-simplified .md-textarea,
      .lod-simplified .help-tooltip,
      .lod-simplified .md-tooltip,
      .lod-simplified .nodeId,
      .lod-simplified .migration-warning,
      .lod-simplified .expiry-container,
      .lod-simplified .title-input,
      .lod-simplified .collapse-expand-button,
      .lod-simplified .ungroup-button,
      .lod-simplified [data-lod-complex],
      .lod-simplified [v-if],
      .lod-simplified .v-if,
      .lod-simplified .control-container,
      .lod-simplified .input-control,
      .lod-simplified .output-control {
        display: none !important;
      }

      /* Optionally show only essential elements in simplified mode */
      .lod-simplified .node-title,
      .lod-simplified .node-header {
        display: block !important;
      }
    `;

    const styleSheet = document.createElement('style');
    styleSheet.id = 'lod-plugin-styles';
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
  }

  // Public API methods
  enable() {
    this.isEnabled = true;
    this.debouncedUpdate();
  }

  disable() {
    this.isEnabled = false;
    // Reset all nodes to full detail
    this.editor.nodes.forEach(node => {
      const nodeView = this.editor.view.nodes.get(node);
      if (nodeView && nodeView.el) {
        this.setNodeVisibility(nodeView.el, node,  true);
        this.setNodeSimplification(nodeView.el, node, false);
        // Reset LOD state
        this.notifyVueComponent(node, false, true); // Set hasBeenRenderedComplex to true when disabling
      }
    });
  }

  updateOptions(newOptions) {
    this.options = { ...this.options, ...newOptions };
    this.debouncedUpdate();
  }

  // Force immediate update (useful for debugging)
  forceUpdate() {
    this.updateLod();
  }

  // Get case size information for debugging and monitoring
  getCaseSizeInfo() {
    const totalNodes = this.editor.nodes ? this.editor.nodes.length : 0;
    const structuralNodes = this.editor.nodes ? 
      this.editor.nodes.filter(node => STRUCTURAL_NODE_TYPES.includes(node.name)).length : 0;
    const regularNodes = totalNodes - structuralNodes;
    const isSmallCase = this.isSmallCase();
    
    return {
      totalNodes,
      structuralNodes,
      regularNodes,
      isSmallCase,
      smallCaseThreshold: this.options.smallCaseThreshold,
      adaptiveCaseSizeEnabled: this.options.enableAdaptiveCaseSize,
      simplificationDisabledDueToSize: isSmallCase && this.options.enableAdaptiveCaseSize,
      viewportCullingStillActive: this.options.enableViewportCulling
    };
  }

  // Get performance metrics
  getPerformanceMetrics() {
    const totalNodes = this.editor.nodes.length;
    const nodesWithComplexState = Array.from(this.nodeStates.values())
      .filter(state => state.hasBeenRenderedComplex).length;
    
    return {
      ...this.performanceMetrics,
      totalNodes,
      nodesWithComplexState,
      cacheEfficiency: this.performanceMetrics.cacheHits / 
        (this.performanceMetrics.cacheHits + this.performanceMetrics.cacheMisses) || 0,
      avgUpdateTime: this.performanceMetrics.lastUpdateTime
    };
  }

  // Reset performance metrics
  resetPerformanceMetrics() {
    this.performanceMetrics = {
      directCssManipulations: 0,
      vueReactivityTriggers: 0,
      cacheHits: 0,
      cacheMisses: 0,
      lastUpdateTime: 0,
      interleavedBatches: 0,
      nodesRenderedProgressively: 0
    };
  }

  // ========================
  // Interleaved Rendering Methods
  // ========================

  initializeInterleavedRendering() {
    // Start the progressive rendering loop
    this.startRenderLoop();
  }

  setupInteractionDetection() {
    const container = this.editor.view.container;
    if (!container) return;

    // Track various user interactions
    const interactionEvents = ['mousedown', 'mousemove', 'wheel', 'touchstart', 'touchmove'];
    
    interactionEvents.forEach(eventType => {
      container.addEventListener(eventType, () => {
        this.lastInteractionTime = performance.now();
        this.userInteractionDetected = true;
      }, { passive: true });
    });

    // Reset interaction flag after a delay
    setInterval(() => {
      const timeSinceLastInteraction = performance.now() - this.lastInteractionTime;
      if (timeSinceLastInteraction > this.options.batchDelayMs * 2) {
        this.userInteractionDetected = false;
      }
    }, this.options.batchDelayMs);
  }

  addToRenderQueue(node) {
    // Prioritize selected nodes
    if (this.options.prioritizeSelectedNodes && this.editor.selected.contains(node)) {
      // Add to front of queue (convert Set to Array, manipulate, convert back)
      const queueArray = Array.from(this.renderQueue);
      queueArray.unshift(node.id);
      this.renderQueue = new Set(queueArray);
    } else {
      this.renderQueue.add(node.id);
    }

    // Start processing if not already running
    if (!this.isProcessingQueue && this.renderQueue.size > 0) {
      this.processRenderQueue();
    }
  }

  startRenderLoop() {
    // Use requestAnimationFrame for smooth rendering
    const renderLoop = () => {
      if (this.renderQueue.size > 0 && !this.userInteractionDetected) {
        this.processRenderQueue();
      }
      requestAnimationFrame(renderLoop);
    };
    requestAnimationFrame(renderLoop);
  }

  async processRenderQueue() {
    if (this.isProcessingQueue || this.renderQueue.size === 0) {
      return;
    }

    // Pause rendering during user interactions
    if (this.userInteractionDetected) {
      return;
    }

    this.isProcessingQueue = true;
    const startTime = performance.now();

    try {
      // Process nodes in batches with time budget
      const nodesToProcess = Array.from(this.renderQueue).slice(0, this.options.maxNodesPerBatch);
      
      for (const nodeId of nodesToProcess) {
        // Check if we've exceeded our time budget
        if (performance.now() - startTime > this.options.renderBudgetMs) {
          break;
        }

        // Check if user started interacting
        if (this.userInteractionDetected) {
          break;
        }

        const node = this.editor.nodes.find(n => n.id === nodeId);
        if (!node) {
          this.renderQueue.delete(nodeId);
          continue;
        }

        const nodeView = this.editor.view.nodes.get(node);
        if (!nodeView || !nodeView.el) {
          this.renderQueue.delete(nodeId);
          continue;
        }

        const state = this.nodeStates.get(node.id);
        if (!state) {
          this.renderQueue.delete(nodeId);
          continue;
        }

        // Render complex elements for this node
        await this.renderNodeComplexElements(node, nodeView, state);
        
        this.renderQueue.delete(nodeId);
        this.performanceMetrics.nodesRenderedProgressively++;
      }

      this.performanceMetrics.interleavedBatches++;

    } finally {
      this.isProcessingQueue = false;
    }

    // Schedule next batch if there are more nodes to process
    if (this.renderQueue.size > 0 && !this.userInteractionDetected) {
      setTimeout(() => {
        this.processRenderQueue();
      }, this.options.batchDelayMs);
    }
  }

  async renderNodeComplexElements(node, nodeView, state) {
    // Mark as complex
    state.hasBeenRenderedComplex = true;
    
    // Notify Vue component
    this.notifyVueComponent(node, state.isSimplified, state.hasBeenRenderedComplex);
    
    // Yield to browser to maintain responsiveness
    await this.yieldToBrowser();
  }

  yieldToBrowser() {
    return new Promise(resolve => {
      if (this.userInteractionDetected) {
        // If user is interacting, yield immediately
        setTimeout(resolve, 0);
      } else {
        // Small delay to let other tasks run
        setTimeout(resolve, 1);
      }
    });
  }

  // Get interleaved rendering status
  getInterleavedRenderingStatus() {
    return {
      enabled: this.options.enableInterleavedRendering,
      queueSize: this.renderQueue.size,
      isProcessing: this.isProcessingQueue,
      userInteracting: this.userInteractionDetected,
      batchesProcessed: this.performanceMetrics.interleavedBatches,
      nodesRenderedProgressively: this.performanceMetrics.nodesRenderedProgressively
    };
  }

  // Force process all queued nodes (for testing/debugging)
  async forceProcessRenderQueue() {
    this.userInteractionDetected = false;
    while (this.renderQueue.size > 0) {
      await this.processRenderQueue();
      // Small delay to prevent blocking
      await new Promise(resolve => setTimeout(resolve, 1));
    }
  }

  // Get current viewport padding information (for debugging)
  getViewportPaddingInfo() {
    const basePadding = this.options.viewportPadding;
    const dynamicPadding = this.calculateDynamicViewportPadding(basePadding);
    const multiplier = dynamicPadding / basePadding;
    const velocityBoost = this.options.enableAdaptivePadding ? this.calculateVelocityBoost() : 1.0;
    
    return {
      basePadding,
      dynamicPadding: Math.round(dynamicPadding),
      multiplier: Math.round(multiplier * 100) / 100,
      velocityBoost: Math.round(velocityBoost * 100) / 100,
      currentZoom: Math.round(this.currentZoom * 100) / 100,
      panVelocity: Math.round(this.currentPanVelocity * 1000) / 1000, // Convert to pixels/ms
      adaptivePaddingEnabled: this.options.enableAdaptivePadding,
      dynamicPaddingEnabled: this.options.enableDynamicPadding,
      viewport: {
        x: Math.round(this.viewport.x),
        y: Math.round(this.viewport.y),
        width: Math.round(this.viewport.width),
        height: Math.round(this.viewport.height)
      },
      bufferedViewport: {
        x: Math.round(this.viewport.x - dynamicPadding),
        y: Math.round(this.viewport.y - dynamicPadding),
        width: Math.round(this.viewport.width + (dynamicPadding * 2)),
        height: Math.round(this.viewport.height + (dynamicPadding * 2))
      }
    };
  }

  destroy() {
    // Clean up interleaved rendering
    if (this.options.enableInterleavedRendering) {
      this.renderQueue.clear();
      this.isProcessingQueue = false;
    }

    // Clean up event listeners
    window.removeEventListener('resize', this.debouncedUpdate);
    
    // Remove injected styles
    const styleSheet = document.getElementById('lod-plugin-styles');
    if (styleSheet) {
      styleSheet.remove();
    }
    
    // Reset all nodes
    this.disable();
  }
}

// Plugin installation function
function install(editor, options) {
  const lodPlugin = new LodPlugin(editor, options);
  editor.lodPlugin = lodPlugin;
  
  // Add convenience methods to editor
  editor.enableLod = () => lodPlugin.enable();
  editor.disableLod = () => lodPlugin.disable();
  editor.updateLodOptions = (options) => lodPlugin.updateOptions(options);
  editor.getLodPerformanceMetrics = () => lodPlugin.getPerformanceMetrics();
  editor.resetLodPerformanceMetrics = () => lodPlugin.resetPerformanceMetrics();
  editor.getInterleavedRenderingStatus = () => lodPlugin.getInterleavedRenderingStatus();
  editor.getViewportPaddingInfo = () => lodPlugin.getViewportPaddingInfo();
  editor.forceProcessRenderQueue = () => lodPlugin.forceProcessRenderQueue();
  editor.getLodCaseSizeInfo = () => lodPlugin.getCaseSizeInfo();
}

export default { install };
export { LodPlugin };
